import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Shield, Plus, Trash2, X, Check, Lock } from 'lucide-react'
import {
  getCapabilityCatalog, listRoles, createRole, updateRole, deleteRole,
  type RoleDef,
} from '@/api/roles'
import { cn } from '@/lib/utils'

const inputCls = 'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#EC5B2C]/40'

export default function Roles() {
  const qc = useQueryClient()
  const { data: catalog } = useQuery({ queryKey: ['capability-catalog'], queryFn: getCapabilityCatalog })
  const { data: roles = [] } = useQuery({ queryKey: ['roles'], queryFn: listRoles })
  const [editing, setEditing] = useState<RoleDef | null>(null)
  const [creating, setCreating] = useState(false)

  const caps = catalog?.capabilities ?? []
  const builtins = catalog?.builtin_roles ?? []
  const invalidate = () => { qc.invalidateQueries({ queryKey: ['roles'] }); qc.invalidateQueries({ queryKey: ['my-capabilities'] }) }

  const delMut = useMutation({
    mutationFn: (id: string) => deleteRole(id),
    onSuccess: invalidate,
    onError: (e: any) => alert(e?.response?.data?.detail || 'Failed to delete'),
  })

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Shield className="h-6 w-6 text-[#EC5B2C]" />
          <div>
            <h1 className="text-xl font-bold text-gray-900">Roles &amp; Permissions</h1>
            <p className="text-sm text-gray-500">Create roles and choose exactly what each one can do.</p>
          </div>
        </div>
        <button onClick={() => setCreating(true)} className="flex items-center gap-1.5 rounded-lg bg-[#EC5B2C] px-3 py-2 text-sm font-medium text-white">
          <Plus className="h-4 w-4" /> New Role
        </button>
      </div>

      {/* Custom roles */}
      <div className="rounded-xl bg-white p-5 shadow-sm ring-1 ring-black/5">
        <h2 className="mb-3 text-sm font-semibold text-gray-700">Custom Roles</h2>
        {roles.length === 0 ? (
          <p className="text-sm text-gray-400">No custom roles yet. Create one to grant a tailored set of permissions.</p>
        ) : (
          <div className="space-y-2">
            {roles.map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-3 rounded-lg border border-gray-100 p-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ background: r.color || '#94a3b8' }} />
                    <span className="font-medium text-gray-900">{r.label}</span>
                    <span className="text-xs text-gray-400">{r.key}</span>
                  </div>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {r.capabilities.length === 0 && <span className="text-xs text-gray-400">No permissions</span>}
                    {r.capabilities.map((c) => (
                      <span key={c} className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] text-gray-600">
                        {caps.find((x) => x.key === c)?.label ?? c}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <button onClick={() => setEditing(r)} className="rounded-md border border-gray-200 px-2.5 py-1 text-xs text-gray-600 hover:bg-gray-50">Edit</button>
                  <button onClick={() => { if (confirm(`Delete role "${r.label}"?`)) delMut.mutate(r.id!) }} className="p-1.5 text-gray-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Built-in roles (reference) */}
      <div className="rounded-xl bg-white p-5 shadow-sm ring-1 ring-black/5">
        <h2 className="mb-1 flex items-center gap-1.5 text-sm font-semibold text-gray-700"><Lock className="h-3.5 w-3.5 text-gray-400" /> Built-in Roles</h2>
        <p className="mb-3 text-xs text-gray-400">These come with the app and can't be edited.</p>
        <div className="space-y-2">
          {builtins.map((r) => (
            <div key={r.key} className="flex flex-wrap items-center gap-2 rounded-lg border border-gray-50 bg-gray-50/50 p-3">
              <span className="w-28 shrink-0 font-medium text-gray-700">{r.label}</span>
              <div className="flex flex-wrap gap-1">
                {r.key === 'admin' ? (
                  <span className="rounded-full bg-[#EC5B2C]/10 px-2 py-0.5 text-[10px] font-medium text-[#EC5B2C]">Full access</span>
                ) : r.capabilities.length === 0 ? (
                  <span className="text-xs text-gray-400">Standard member</span>
                ) : r.capabilities.map((c) => (
                  <span key={c} className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] text-gray-600">{caps.find((x) => x.key === c)?.label ?? c}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {(creating || editing) && (
        <RoleModal
          role={editing}
          caps={caps}
          onClose={() => { setCreating(false); setEditing(null) }}
          onSaved={() => { invalidate(); setCreating(false); setEditing(null) }}
        />
      )}
    </div>
  )
}

function RoleModal({ role, caps, onClose, onSaved }: {
  role: RoleDef | null
  caps: { key: string; label: string; description: string }[]
  onClose: () => void
  onSaved: () => void
}) {
  const [label, setLabel] = useState(role?.label ?? '')
  const [color, setColor] = useState(role?.color ?? '#7c3aed')
  const [selected, setSelected] = useState<Set<string>>(new Set(role?.capabilities ?? []))
  const [error, setError] = useState('')

  const toggle = (k: string) => setSelected((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n })

  const mut = useMutation({
    mutationFn: () => {
      const payload = { label: label.trim(), capabilities: [...selected], color }
      return role?.id ? updateRole(role.id, payload) : createRole(payload)
    },
    onSuccess: onSaved,
    onError: (e: any) => setError(e?.response?.data?.detail || 'Failed to save'),
  })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="max-h-[88vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b px-5 py-3">
          <div className="font-semibold text-gray-900">{role ? 'Edit Role' : 'New Role'}</div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
        </div>
        <div className="space-y-4 p-5">
          {error && <div className="rounded bg-red-50 px-3 py-2 text-sm text-red-600">{error}</div>}
          <div className="flex items-end gap-3">
            <label className="flex-1">
              <span className="mb-1 block text-xs font-medium text-gray-600">Role name</span>
              <input className={inputCls} placeholder="e.g. Supervisor" value={label} onChange={(e) => setLabel(e.target.value)} />
            </label>
            <label>
              <span className="mb-1 block text-xs font-medium text-gray-600">Color</span>
              <input type="color" className="h-9 w-12 cursor-pointer rounded border border-gray-200" value={color} onChange={(e) => setColor(e.target.value)} />
            </label>
          </div>

          <div>
            <span className="mb-2 block text-xs font-medium text-gray-600">Permissions</span>
            <div className="space-y-1.5">
              {caps.map((c) => {
                const on = selected.has(c.key)
                return (
                  <button key={c.key} type="button" onClick={() => toggle(c.key)}
                    className={cn('flex w-full items-start gap-3 rounded-lg border p-3 text-left transition',
                      on ? 'border-[#EC5B2C] bg-[#EC5B2C]/5' : 'border-gray-200 hover:bg-gray-50')}>
                    <span className={cn('mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                      on ? 'border-[#EC5B2C] bg-[#EC5B2C] text-white' : 'border-gray-300')}>
                      {on && <Check className="h-3 w-3" />}
                    </span>
                    <span>
                      <span className="block text-sm font-medium text-gray-800">{c.label}</span>
                      <span className="block text-xs text-gray-500">{c.description}</span>
                    </span>
                  </button>
                )
              })}
            </div>
            {selected.has('admin_settings') && (
              <p className="mt-2 rounded bg-amber-50 px-3 py-2 text-xs text-amber-700">Admin Settings grants full access to everything.</p>
            )}
          </div>

          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-600">Cancel</button>
            <button onClick={() => mut.mutate()} disabled={!label.trim() || mut.isPending} className="rounded-lg bg-[#EC5B2C] px-4 py-2 text-sm font-medium text-white disabled:opacity-60">
              {role ? 'Save' : 'Create Role'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
